import { minBy } from 'es-toolkit';
import React from 'react';
import { BoxGeometry, ExtrudeGeometry, Shape, WireframeGeometry } from 'three';

import { Bearing, BearingType, Component, GeneratableBearing } from '@paragon/contract-library';
import { Point2D } from '@paragon/geometry-library';
import { calculateMemberGeometry } from '@paragon/relative-geometry';
import {
  BearingVisualData,
  calculateBearingVisualData,
  calculateGeneratableBearingVisualData,
  RaycastOrder,
} from '@paragon/three-shared';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { handleStandardSceneItemRightClick, leftClickItem } from '../../store/actions/sceneInteractionActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../store/actions/viewportActions';
import { getHangerToDisplayOnBearing } from '../../store/selectors/bearingHangerSelectors';
import { BearingDimensions } from '../dimension/BearingDimensions';
import { JackHangerVisual } from '../layout/HangerVisual';

const doublePlateGeometry = new WireframeGeometry(new BoxGeometry(1, 1, 1, 1, 2, 1));
const genericBearingGeometry = new WireframeGeometry(new BoxGeometry(1, 1, 1, 1, 1, 1));
const hangerGeometry = (() => {
  const hangerThickness = 0.2;
  const shape = new Shape();
  shape.lineTo(0, 1);
  shape.lineTo(hangerThickness, 1);
  shape.lineTo(hangerThickness, hangerThickness);
  shape.lineTo(1, 0 + hangerThickness);
  shape.lineTo(1, 0);
  shape.lineTo(0, 0);
  const extrudePadding = 0.1;
  const extrudeSettings = {
    steps: 1,
    depth: 1 + extrudePadding,
    bevelEnabled: false,
  };
  return new WireframeGeometry(new ExtrudeGeometry(shape, extrudeSettings)).center();
})();

const defaultColor = 0x0083f5;
const selectedColor = 0xe74c3c;

interface BearingVisualProps {
  readonly visualData: BearingVisualData | null;
  readonly depth: number;
  readonly isContinuous: boolean;
  readonly bearingType: BearingType;
  readonly component?: Component;
  readonly selected?: boolean;
  readonly bearingViewStatus?: 'Hidden' | 'Normal';
}

export function BearingBaseVisual(props: BearingVisualProps) {
  const { visualData, depth, isContinuous, bearingType, component } = props;
  const { selected = false, bearingViewStatus = 'Normal' } = props;

  if (!visualData) return null;
  const { position, width, height, normal } = visualData;

  let hangerBump = 0;
  let hangerRotation = 0;
  if (bearingType === 'Hanger' && component != null) {
    hangerBump = 1;
    if (isHangerSupportSideFromRight(position, component)) {
      hangerRotation = Math.PI;
    }
  }

  const { geometry, dispose } = getGeometryFromStatus(bearingType, isContinuous, width);
  const visible = bearingViewStatus === 'Normal';

  return (
    <group
      scale={[width, height, depth]}
      position={[position.x, position.y, 0]}
      rotation-z={normal.rotateReverseQuarterTurn().angleInRadians}
      visible={visible}
    >
      <lineSegments position={[0, hangerBump - 1 / 2, 1 / 2]} rotation-y={hangerRotation}>
        <primitive object={geometry} attach={'geometry'} dispose={dispose} />
        <lineBasicMaterial color={selected ? selectedColor : defaultColor} />
      </lineSegments>
    </group>
  );
}

function getGeometryFromStatus(bearingType: BearingType, isContinuous: boolean, width: number) {
  if (isContinuous && (bearingType === 'DoublePlate' || bearingType === 'ConcreteWall')) {
    return {
      geometry: new WireframeGeometry(new BoxGeometry(1, 1, 1, Math.ceil(width / 6), 1, 1)),
      dispose: undefined,
    };
  }

  switch (bearingType) {
    case 'DoublePlate':
      return { geometry: doublePlateGeometry, dispose: null };
    case 'Hanger':
      return { geometry: hangerGeometry, dispose: null };
    case 'ConcreteWall':
    case 'Ledger':
    case 'Header':
    case 'ToeNail':
      return { geometry: genericBearingGeometry, dispose: null };
    default:
      assertExhaustiveSwitch(bearingType);
  }
}

export function isHangerSupportSideFromRight(position: Point2D, component: Component) {
  // heuristic: is the bearing positioned to the left or right of the middle of the chord with the nearest vertex?
  const chords = component.members.filter(
    (member) => member.memberType === 'TopChord' || member.memberType === 'BottomChord',
  );
  const closestChord = minBy(chords, (chord) => {
    const geometry = calculateMemberGeometry(chord, component) ?? [];
    return Math.min(...geometry.map((point) => position.distanceTo(Point2D.fromObject(point))));
  });
  if (!closestChord) return position.x > component.span / 2;
  const geometry = calculateMemberGeometry(closestChord, component) ?? [];
  const chordMidX = (Math.max(...geometry.map((point) => point.x)) + Math.min(...geometry.map((point) => point.x))) / 2;
  return position.x > chordMidX;
}

export const BearingVisual = ({ bearing, component }: { bearing: Bearing; component: Component }) => {
  const dispatch = useAppDispatch();
  const selectedItems = useAppSelector(getSelectedItemIdentifiers);
  const selected = selectedItems.some((item) => item.type === 'bearing' && item.guid === bearing.guid);

  const bearingViewStatus = useAppSelector((state) => state.visualOptions.hiddenObjectGuids).includes(bearing.guid)
    ? 'Hidden'
    : 'Normal';

  const visualData = calculateBearingVisualData(bearing, component);

  const hardwareToDisplay = useAppSelector(getHangerToDisplayOnBearing)(bearing.guid);
  if (hardwareToDisplay != null) {
    return (
      visualData && (
        <JackHangerVisual
          hardware={hardwareToDisplay}
          component={component}
          visualData={visualData}
          bearing={bearing}
        />
      )
    );
  }

  const item = { type: 'bearing', guid: bearing.guid, origin: 'fiber' } as const;

  return (
    <>
      <group
        onClick={(event) => dispatch(leftClickItem(event, { item: bearing, type: 'bearing' }))}
        onContextMenu={async (event) => await dispatch(handleStandardSceneItemRightClick(event, item))}
        onPointerOver={(event) => {
          event.stopPropagation();
          dispatch(ensureHoverItemIsActive(item));
        }}
        onPointerOut={() => dispatch(clearHoverItemIfActive(item))}
        userData-raycastOrder={RaycastOrder.BearingVisual}
      >
        <BearingBaseVisual
          bearingType={bearing.bearingType}
          component={component}
          depth={bearing.depth}
          isContinuous={bearing.relativeGeometry?.bearingGeometryType === 'ContinuousBearing'}
          visualData={visualData}
          selected={selected}
          bearingViewStatus={bearingViewStatus}
        />
      </group>
      {selected && <BearingDimensions bearingToEdit={bearing} visualData={visualData} />}
    </>
  );
};

export const GeneratableBearingVisual = (props: { bearing: GeneratableBearing; span: number }) => {
  const { bearing, span } = props;
  const visualData = calculateGeneratableBearingVisualData(bearing, span);

  return (
    visualData && (
      <BearingBaseVisual
        bearingType={bearing.bearingType}
        depth={3.5}
        isContinuous={'isContinuous' in bearing && bearing.isContinuous}
        visualData={visualData}
      />
    )
  );
};
